import {useLayoutEffect, useRef, useState} from 'react';

import {assert} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';
import {
  createTableInteractionController,
  TableInteractionController,
  TableInteractionHandle,
  TableInteractionParams,
} from 'notebook/tables/store/logic/interactions/createTableInteractionController';

/**
 * Provides the table's {@link TableInteractionHandle} to its cells. The value never changes for a
 * table's lifetime, so reading it does not re-render a cell.
 */
export const TableInteractionContext = makeReactContext<TableInteractionHandle>(
  () => {
    assert(false, 'TableInteractionContext must be used within TableEditable');
  },
  'TableInteractionContext',
  Object.is
);

export function useTableInteractionHandle(): TableInteractionHandle {
  return TableInteractionContext.useContext();
}

/**
 * Builds the table's interaction controller once. `getTableModel`, `getTableProps`, `setState` and
 * the refs must be stable; `multiFinalizeEditHandler` may change on every render and the controller
 * always calls the latest one.
 */
export function useTableInteractionController({
  getTableModel,
  getTableProps,
  setState,
  multiFinalizeEditHandler,
  tableRootRef,
  verticalScrollContainerRef,
}: TableInteractionParams): TableInteractionController {
  const multiFinalizeEditHandlerRef = useRef(multiFinalizeEditHandler);
  useLayoutEffect(() => {
    multiFinalizeEditHandlerRef.current = multiFinalizeEditHandler;
  }, [multiFinalizeEditHandler]);

  const [controller] = useState(() =>
    createTableInteractionController({
      getTableModel,
      getTableProps,
      setState,
      multiFinalizeEditHandler: () => {
        multiFinalizeEditHandlerRef.current();
      },
      tableRootRef,
      verticalScrollContainerRef,
    })
  );
  useLayoutEffect(() => controller.mount(), [controller]);
  return controller;
}
